import { useApp } from '@context/AppContext';
import PlayerAvatar from './PlayerAvatar';
import { X } from 'lucide-react';

/**
 * Shared lobby shell used by both Challenge and Relay lobbies.
 * Renders player list + settings area (host-editable) + action buttons.
 */
export default function GameLobby({
  title,
  emoji,
  children,
  onStart,
  onCancel,
  startLabel = 'Start',
  startDisabled = false,
}) {
  const { state } = useApp();
  const { room, user } = state;
  const members = Object.entries(room.members || {}).sort(([, a], [, b]) => a.joinedAt - b.joinedAt);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h2 className="text-lg font-semibold text-charcoal font-display">
            {emoji && <span className="mr-2">{emoji}</span>}
            {title}
          </h2>
          <button
            onClick={onCancel}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-charcoal hover:bg-slate-100 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Player list */}
          <div>
            <p className="text-xs text-slate-400 font-display uppercase tracking-wide mb-2">
              Players ({members.length})
            </p>
            <div className="flex flex-wrap gap-2">
              {members.map(([id, member]) => (
                <div
                  key={id}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-display ${
                    id === user.id ? 'bg-amber/10 border border-amber/30' : 'bg-slate-50 border border-slate-100'
                  }`}
                >
                  <PlayerAvatar nickname={member.nickname} colour={member.colour} size="sm" />
                  {id === user.id && <span className="text-[10px] text-amber font-mono ml-1">you</span>}
                </div>
              ))}
            </div>
          </div>

          {/* Mode-specific settings (passed as children) */}
          {children}
        </div>

        {/* Footer actions */}
        <div className="px-5 py-4 border-t border-slate-100 flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 text-sm font-display text-slate-500 hover:bg-slate-50 transition-colors"
          >
            Cancel
          </button>
          {user.isHost ? (
            <button
              onClick={onStart}
              disabled={startDisabled}
              className="flex-1 px-4 py-2.5 rounded-xl bg-amber text-white text-sm font-semibold font-display hover:bg-amber/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {startLabel}
            </button>
          ) : (
            <div className="flex-1 px-4 py-2.5 rounded-xl bg-slate-50 text-sm font-display text-slate-400 text-center">
              Waiting for host...
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
